/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Injectable
} from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	Router,
	UrlTree
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	DynamicComponentLookup
} from '@dynamicComponents/dynamic-component.lookup';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	WorkItemConstants
} from '@shared/constants/work-item-constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	IEntityNavigationDefinition
} from '@shared/interfaces/application-objects/entity-navigation-definition.interface';
import {
	IEntityNavigationRequest
} from '@shared/interfaces/application-objects/entity-navigation-request.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	IWorkItem
} from '@shared/interfaces/workItems/work-item.interface';
import {
	NavigationService
} from '@shared/services/navigation.service';

/**
 * A route guard that translates simplified work item link into
 * a fully defined route to its parent entity reference.
 *
 * Expected URL pattern:
 * `workItems/item/:workItemType/:id`
 *
 * @export
 * @class WorkItemParentDeepLinkTranslatorGuard
 * @implements {CanActivate}
 */
@Injectable({
	providedIn: 'root'
})
export class WorkItemParentDeepLinkTranslatorGuard implements CanActivate
{
	/**
	 * Creates an instance of WorkItemParentDeepLinkTranslatorGuard.
	 *
	 * @param {Router} router
	 * The router used to create redirect URL trees.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type API service used to resolve work item types.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance API service used to resolve work item instances.
	 * @param {NavigationService} navigationService
	 * The centralized navigation service used to resolve navigation definitions.
	 * @memberof WorkItemParentDeepLinkTranslatorGuard
	 */
	public constructor(
		private readonly router: Router,
		private readonly entityTypeApiService: EntityTypeApiService,
		private readonly entityInstanceApiService: EntityInstanceApiService,
		private readonly navigationService: NavigationService)
	{
	}

	/**
	 * Resolves a work item link and redirects to the associated
	 * parent entity route with work item drawer context.
	 *
	 * @async
	 * @param {ActivatedRouteSnapshot} route
	 * The activated route snapshot containing route parameters.
	 * @returns {Promise<boolean | UrlTree>}
	 * A URL tree redirect on success, or a fallback URL tree when unresolved.
	 * @memberof WorkItemParentDeepLinkTranslatorGuard
	 */
	public async canActivate(
		route: ActivatedRouteSnapshot): Promise<boolean | UrlTree>
	{
		try
		{
			const workItemTypeToken: string =
				route.paramMap.get('workItemType');
			const idValue: string =
				route.paramMap.get('id');
			const workItemId: number = Number(idValue);

			if (AnyHelper.isNullOrWhitespace(workItemTypeToken)
				|| !Number.isInteger(workItemId)
				|| workItemId <= 0)
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ 'The work item route format was invalid.');
			}

			const workItemType: IEntityType =
				await this.getWorkItemType(workItemTypeToken);

			if (AnyHelper.isNull(workItemType))
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ `The work item type '${workItemTypeToken}' was not found.`);
			}

			const workItem: IWorkItem =
				await this.getWorkItemById(
					workItemId,
					workItemType);

			if (AnyHelper.isNull(workItem))
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ `A work item with id '${workItemId}' was not found.`);
			}

			const parentEntityTypeName: string =
				workItem.data.references
					.find(
						(reference: {type: string; identifier: string}) =>
							reference.type ===
								WorkItemConstants.workItemIdentifiers
									.parentNavigationEntityType)
					?.identifier;

			const parentEntityIdValue: string =
				workItem.data.references
					.find(
						(reference: {type: string; identifier: string}) =>
							reference.type ===
								WorkItemConstants.workItemIdentifiers
									.parentNavigationEntityIdentifer)
					?.identifier;

			if (AnyHelper.isNullOrWhitespace(parentEntityTypeName)
				|| AnyHelper.isNullOrWhitespace(parentEntityIdValue)
				|| !/^\d+$/.test(parentEntityIdValue))
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ 'This work item is missing parent navigation metadata.');
			}

			const parentEntityId: number = Number.parseInt(
				parentEntityIdValue,
				AppConstants.parseRadix);

			const parentEntityType: IEntityType =
				await this.entityTypeApiService.getSingleQueryResult(
					`Name eq '${parentEntityTypeName.replaceAll("'", "''")}'`, //Is this really needed?
					AppConstants.empty,
					true);

			if (AnyHelper.isNull(parentEntityType))
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ `The parent entity type '${parentEntityTypeName}' was not found.`);
			}

			const navigationDefinition: IEntityNavigationDefinition =
				await this.navigationService.getEntityNavigationDefinition(
					<IEntityNavigationRequest>
					{
						id: parentEntityId,
						entityType: parentEntityType.name,
						entityTypeGroup: parentEntityType.group
					});

			const routeData: any =
				{
					layoutType: navigationDefinition.layoutType,
					activeDrawerComponent:
						DynamicComponentLookup.supportedTypes.workItemsComponent,
					activeDrawerItemDescription:
						`|${WorkItemConstants.workItemIdentifiers.workItemIdentifier}`
							+ `:${workItem.id}|`,
					previousRouteData: AppConstants.empty,
					...navigationDefinition.additionalRouteData
				};

			const isGeneratedLayout: boolean =
				navigationDefinition.layoutType
					=== AppConstants.layoutTypes.generated;

			const viewType: string =
				isGeneratedLayout
					? AppConstants.viewTypes.view
					: AppConstants.viewTypes.edit;

			return this.router.createUrlTree(
				[
					navigationDefinition.baseUrl,
					navigationDefinition.entityTypeGroup,
					viewType,
					navigationDefinition.id
				],
				{
					queryParams: {
						routeData: ObjectHelper.mapRouteData(routeData)
					}
				});
		}
		catch
		{
			return this.getFallbackRoute(
				'Unable to open this link. An unexpected error occurred '
					+ 'while translating the work item route.');
		}
	}

	/**
	 * Resolves and returns a work item by id in the specified work item type.
	 *
	 * @private
	 * @async
	 * @param {string} workItemTypeToken
	 * The incoming work item type token from the route.
	 * @returns {Promise<IEntityType>}
	 * The resolved work item type, or null if not found.
	 * @memberof WorkItemParentDeepLinkTranslatorGuard
	 */
	private async getWorkItemType(
		workItemTypeToken: string): Promise<IEntityType>
	{
		const workItemType: IEntityType =
			await this.entityTypeApiService.getSingleQueryResult(
				`Name eq '${workItemTypeToken}'`,
				AppConstants.empty,
				true);

		if (AnyHelper.isNull(workItemType)
			|| !workItemType.group.startsWith(
				WorkItemConstants.workItemEntityTypePrefix))
		{
			return null;
		}

		return workItemType;
	}

	/**
	 * Resolves and returns a work item by id in the specified work item type.
	 *
	 * @private
	 * @async
	 * @param {number} workItemId
	 * The work item identifier to resolve.
	 * @param {IEntityType} workItemType
	 * The resolved work item entity type.
	 * @returns {Promise<IWorkItem>}
	 * The resolved work item, or null if not found.
	 * @memberof WorkItemParentDeepLinkTranslatorGuard
	 */
	private async getWorkItemById(
		workItemId: number,
		workItemType: IEntityType): Promise<IWorkItem>
	{
		const apiService: EntityInstanceApiService =
			this.entityInstanceApiService.GetNewService();

		apiService.entityInstanceTypeGroup = workItemType.group;

		try
		{
			const workItem: IEntityInstance =
				await apiService.get(workItemId);

			return AnyHelper.isNull(workItem)
				? null
				: <IWorkItem>workItem;
		}
		catch
		{
			return null;
		}
	}

	/**
	 * Returns a fallback work items dashboard route when translation fails.
	 *
	 * @private
	 * @param {string} bannerMessage
	 * The user-facing message to display before redirecting.
	 * @returns {UrlTree}
	 * The fallback work items dashboard URL tree.
	 * @memberof WorkItemParentDeepLinkTranslatorGuard
	 */
	private getFallbackRoute(
		bannerMessage: string): UrlTree
	{
		EventHelper.dispatchBannerEvent(
			'Invalid Work Item Link',
			`${bannerMessage} Redirecting to the work items dashboard.`,
			AppConstants.activityStatus.error);

		return this.router.createUrlTree(
			[
				'workItems',
				AppConstants.route.dashboardPage
			]);
	}
}
